import { EFinancingProgram, EWorksheetDecision } from '@if/core/types'
import { Button, Stack, Typography } from '@mui/material'
import { Breadcrumb, PageError, PageSpinner } from '@src/components'
import PendingMessage from '@src/components/PendingMessage'
import PrivateMessage from '@src/containers/ViewCreditApplicationPage/components/privateMessage'
import WarningMessageAccordion from '@src/containers/ViewCreditApplicationPage/components/warningMessageAccordion'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useUpdateEasyCareWorksheetDecision } from '@src/data/api/easycare-worksheet/easycare-worksheet-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import { useDeleteMessage } from '@src/data/api/message/message-api'
import { useBusinessErrors } from '@src/data/selectors/app-config-selectors'
import {
  buildParameterizedNormMessage,
  useAreAllTasksRequiredForCVTCompleted,
  useCurrentCreditApplicationSpecificNorms,
  useIncomeConfirmed,
  useIsFinalDecisionPending,
} from '@src/data/selectors/credit-selectors'
import { useAreAllDocumentsRequiredForCVT } from '@src/data/selectors/document-selectors'
import {
  BaseWorksheetFundingThresholdExtend,
  CreditApplicationMessage,
  NormsMessage,
  WorksheetDecision,
} from '@src/data/types'
import { EasyCareWorksheet, EasyCareWorksheetDecisionDTO } from '@src/data/types/EasyCareWorksheetSchema'
import { useUser } from '@src/providers/UserProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import WorksheetDecisionDialog from '../components/LoanSummary/WorksheetDecisionDialog'
import { useDialogDecisionDefaultValue } from '../viewWorksheet-hooks'
import EasyCareDetailCards from './components/EasyCareDetailCards'
import EasyCareSummaryBand, { isDecisionDisabled } from './components/EasyCareSummaryBand'

const EasyCareWorksheetViewPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    creditApplication,
    worksheet: contextWorksheet,
    isLoading,
    documents,
    requiredDocuments,
    privateMessages,
  } = useCreditApplicationData()
  const worksheet = contextWorksheet as EasyCareWorksheet | undefined
  const error = useBusinessErrors()
  const user = useUser()

  const [updateDecision] = useUpdateEasyCareWorksheetDecision()
  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()
  const [deleteMessage] = useDeleteMessage()
  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false)

  const finalDecisionIsPending = useIsFinalDecisionPending()
  const areAllTasksRequiredForCVTCompleted = useAreAllTasksRequiredForCVTCompleted()
  const areAllDocumentsRequiredForCVTApproved = useAreAllDocumentsRequiredForCVT(documents, requiredDocuments)
  const incomeConfirmed = useIncomeConfirmed()
  const commonNormsMessage: NormsMessage[] = useCurrentCreditApplicationSpecificNorms() ?? []

  const dialogDecisionDefaultValue = useDialogDecisionDefaultValue(
    creditApplication!,
    worksheet!,
    areAllTasksRequiredForCVTCompleted,
    areAllDocumentsRequiredForCVTApproved,
    incomeConfirmed,
  )

  const worksheetNormWarnings = useMemo<NormsMessage[]>(
    () =>
      (worksheet?.normsDecision?.warningMessages ?? []).map(
        (wr) => ({ id: wr.id, message: buildParameterizedNormMessage(wr), applicantType: null }) as NormsMessage,
      ),
    [worksheet?.normsDecision?.warningMessages],
  )

  const closeDecisionDialog = useCallback(() => setDecisionDialogOpen(false), [])

  const handleSaveDecision = useCallback(
    async (data: WorksheetDecision) => {
      if (!worksheet || !creditApplication) return

      const updatedFinalDecision: EasyCareWorksheetDecisionDTO = {
        creditApplicationId: worksheet.creditApplicationId,
        decision: data.decision,
        otherReason: data.otherReason,
        versionTag: worksheet.versionTag,
      } as EasyCareWorksheetDecisionDTO

      if (data.decision === EWorksheetDecision.ApprovedWithCondition) {
        const fundingThresholdExtend: BaseWorksheetFundingThresholdExtend = {
          maxPmtAmount: data.maxPmtAmount,
          maxTotalAmountFinanced: data.maxTotalAmountFinanced,
          maxTermDuration: data.maxTermDuration,
        }
        updatedFinalDecision.fundingThresholdExtend = fundingThresholdExtend
      }

      try {
        await updateDecision(updatedFinalDecision)
      } catch (err) {
        reportErrorToConsole(err)
        return
      }
      closeDecisionDialog()
      try {
        await invalidateFullCreditApplication(creditApplication.id, creditApplication.financingProgramId)
      } catch (err) {
        reportErrorToConsole(err)
      }
      navigate(`/Applications/${EFinancingProgram.EasyCareProtectionPlan}/${creditApplication.id}/view`)
    },
    [worksheet, creditApplication, closeDecisionDialog, invalidateFullCreditApplication, updateDecision, navigate],
  )

  const handleDeletingPrivateMessage = useCallback(
    async (message: CreditApplicationMessage) => {
      if (!creditApplication) return
      await deleteMessage({
        id: message.id,
        creditApplicationId: message.creditApplicationId,
        financingProgramId: creditApplication.financingProgramId,
      })
    },
    [creditApplication, deleteMessage],
  )

  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.EasyCareProtectionPlan}/${creditApplication?.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  return (
    <div>
      <PageSpinner isLoading={isLoading} />
      {!isLoading && creditApplication && worksheet && (
        <Stack spacing={2}>
          <Breadcrumb trees={breadCrumbs} />
          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
            <Typography variant="h5" component="h1">
              {t('easyCare.worksheetTitle')}
            </Typography>
            <Button
              variant="outlined"
              href={`/Applications/${EFinancingProgram.EasyCareProtectionPlan}/${creditApplication.id}/worksheet/edit`}
            >
              {t('common.edit')}
            </Button>
          </Stack>
          <PageError errors={error} />
          {finalDecisionIsPending && <PendingMessage />}
          {(commonNormsMessage.length > 0 || worksheetNormWarnings.length > 0) && (
            <WarningMessageAccordion
              applicantCreditWarnings={null}
              coapplicantCreditWarnings={null}
              commonCreditWarnings={commonNormsMessage}
              worksheetWarnings={worksheetNormWarnings}
              expanded={false}
            />
          )}
          <PrivateMessage
            messages={privateMessages}
            handleDeletingPrivateMessage={handleDeletingPrivateMessage}
            currentUser={user}
            creditAppCreationDate={creditApplication.createdOn}
          />
          <EasyCareSummaryBand
            worksheet={worksheet}
            interestRate={creditApplication.finalCreditDecision?.interestRate ?? 0}
            decisionDisabled={isDecisionDisabled({
              status: worksheet.status,
              editLocked: creditApplication.editLocked === true,
              finalDecisionIsPending,
              canEditWorksheet: user?.rights.canEditWorksheet === true,
            })}
            onDecisionClick={() => setDecisionDialogOpen(true)}
          />
          <EasyCareDetailCards worksheet={worksheet} />
          <WorksheetDecisionDialog
            open={decisionDialogOpen}
            title={t('worksheet.decision')}
            onCancel={closeDecisionDialog}
            onConfirm={handleSaveDecision}
            worksheetWarnings={null}
            defaultValue={dialogDecisionDefaultValue}
            worksheet={worksheet}
            creditapplication={creditApplication}
          />
        </Stack>
      )}
    </div>
  )
}

export default EasyCareWorksheetViewPage
